'use client';

import { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import { Sun, Moon, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useThemeStore, useIsDark } from '@/stores/themeStore';
import { AxisLogo, AxisWordmark } from '@/components/ui/CoreOpsLogo';
import {
  Hero,
  ProblemCondensed,
  WhoItsFor,
  FeaturesHighlight,
  PricingPreview,
  DualCTA,
  SectionDivider,
  Footer,
} from '@/components/landing';

const Landing = () => {
  const { setTheme, toggleTheme } = useThemeStore();
  const isDark = useIsDark();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [scrollY, setScrollY] = useState(0);

  // Default to light mode on first visit (user can toggle)
  useEffect(() => {
    const stored = localStorage.getItem('axis-theme');
    if (!stored) setTheme('light');
  }, [setTheme]);

  // Sync status bar color with theme (iOS Safari)
  useEffect(() => {
    const color = isDark ? '#0d0d0d' : '#FAFBFC';
    document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
      meta.setAttribute('content', color);
      meta.removeAttribute('media');
    });
  }, [isDark]);

  // Track scroll position for parallax effects
  const handleScroll = useCallback(() => {
    setScrollY(window.scrollY);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Handle hash-link scrolling from subpages (e.g., /pricing -> /#contact)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }
  }, []);

  const navLinks = [
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Features', href: '/features' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'FAQ', href: '/faq' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFBFC] text-gray-900 dark:bg-[#0d0d0d] dark:text-secondary-100">
      {/* Fixed compass watermark — always visible, z-[5] so it floats above section backgrounds */}
      <div
        className="fixed inset-0 z-[5] flex items-center justify-center pointer-events-none select-none"
        style={{ opacity: isDark ? 0.12 : 0.18 }}
      >
        <img
          src="/images/axis-datum-compass.png"
          alt=""
          className="w-[140vw] h-[140vw] max-w-[2600px] max-h-[2600px] object-contain drop-shadow-[0_0_80px_rgba(59,130,246,0.2)]"
          draggable={false}
        />
      </div>

      {/* Geometric parallax overlay — hidden on mobile */}
      <div
        className="hidden md:block fixed inset-0 z-[1] pointer-events-none select-none overflow-hidden"
        style={{ opacity: Math.min(1, scrollY / 500) }}
      >
        {/* Diagonal accent lines */}
        <div
          className="absolute w-[200%] h-px bg-gradient-to-r from-transparent via-primary-500/15 dark:via-primary-400/20 to-transparent origin-center"
          style={{ top: '25%', left: '-50%', transform: `rotate(-25deg) translateY(${scrollY * -0.15}px)` }}
        />
        <div
          className="absolute w-[200%] h-px bg-gradient-to-r from-transparent via-primary-500/20 dark:via-primary-400/25 to-transparent origin-center"
          style={{ top: '55%', left: '-50%', transform: `rotate(30deg) translateY(${scrollY * -0.1}px)` }}
        />
        <div
          className="absolute w-[200%] h-px bg-gradient-to-r from-transparent via-primary-500/10 dark:via-primary-400/15 to-transparent origin-center"
          style={{ top: '78%', left: '-50%', transform: `rotate(-18deg) translateY(${scrollY * -0.2}px)` }}
        />

        {/* Floating panels */}
        <div
          className="absolute w-36 h-48 rounded-sm border border-primary-500/10 dark:border-primary-400/15 bg-white/10 dark:bg-[#0d0d0d]/30"
          style={{ top: '20%', left: '6%', transform: `translateY(${scrollY * -0.2}px) rotate(-6deg)` }}
        />
        <div
          className="absolute w-44 h-24 rounded-sm border border-primary-500/12 dark:border-primary-400/18 bg-white/8 dark:bg-[#0d0d0d]/25"
          style={{ top: '35%', right: '10%', transform: `translateY(${scrollY * -0.12}px) rotate(4deg)` }}
        />
        <div
          className="absolute w-28 h-52 rounded-sm border border-primary-500/8 dark:border-primary-400/12 bg-white/8 dark:bg-[#0d0d0d]/20"
          style={{ top: '55%', left: '20%', transform: `translateY(${scrollY * -0.28}px) rotate(10deg)` }}
        />
        <div
          className="absolute w-40 h-28 rounded-sm border border-primary-500/10 dark:border-primary-400/15 bg-white/10 dark:bg-[#0d0d0d]/30 backdrop-blur-[1px]"
          style={{ top: '70%', right: '8%', transform: `translateY(${scrollY * -0.18}px) rotate(-5deg)` }}
        />

        {/* Vertical accent lines */}
        <div
          className="absolute w-px h-48 bg-gradient-to-b from-transparent via-primary-500/15 dark:via-primary-400/25 to-transparent"
          style={{ top: '15%', left: '30%', transform: `translateY(${scrollY * 0.08}px)` }}
        />
        <div
          className="absolute w-px h-56 bg-gradient-to-b from-transparent via-primary-500/10 dark:via-primary-400/20 to-transparent"
          style={{ top: '60%', right: '22%', transform: `translateY(${scrollY * 0.1}px)` }}
        />
      </div>

      {/* Floating Navigation */}
      <nav className={cn(
        "fixed top-0 left-0 right-0 z-50",
        "bg-white/80 backdrop-blur-xl dark:bg-[#0d0d0d]/80",
        "border-b border-gray-200/50 dark:border-white/[0.04]",
        "pt-[env(safe-area-inset-top)]"
      )}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3">
              <AxisLogo size="md" />
              <AxisWordmark size="md" />
            </Link>

            {/* Nav Links — Desktop */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm text-gray-500 hover:text-gray-900 dark:text-secondary-400 dark:hover:text-secondary-100 transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTAs + Mobile Menu Toggle */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className={cn(
                  "p-2 rounded-lg transition-all duration-200",
                  "text-gray-400 hover:text-gray-600 dark:text-secondary-500 dark:hover:text-secondary-300",
                  "hover:bg-gray-100 dark:hover:bg-white/[0.06]"
                )}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <Link
                href="/login"
                className="hidden sm:inline text-sm text-gray-500 hover:text-gray-900 dark:text-secondary-400 dark:hover:text-secondary-100 transition-colors"
              >
                Login
              </Link>
              <a
                href="#contact"
                className={cn(
                  "hidden sm:inline-block px-4 py-2 rounded-lg text-sm font-medium",
                  "bg-primary-600 text-white dark:bg-primary-500",
                  "hover:bg-primary-500 dark:hover:bg-primary-400 transition-all duration-300",
                  "shadow-md dark:shadow-[0_0_20px_-5px_rgba(59,130,246,0.4)]"
                )}
              >
                Join the Waitlist
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={cn(
                  "md:hidden p-2 rounded-lg transition-all duration-200",
                  "text-gray-500 hover:text-gray-700 dark:text-secondary-400 dark:hover:text-secondary-200",
                  "hover:bg-gray-100 dark:hover:bg-white/[0.06]"
                )}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={cn(
          "md:hidden overflow-hidden transition-all duration-300 ease-out",
          "border-t border-gray-200/50 dark:border-white/[0.04]",
          mobileMenuOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0 border-t-0"
        )}>
          <div className="px-6 py-4 space-y-1 bg-white/95 dark:bg-[#0d0d0d]/95 backdrop-blur-xl">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-secondary-300 dark:hover:text-secondary-100 transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-gray-200 dark:border-white/[0.06] flex flex-col gap-3">
              <Link
                href="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-secondary-300 dark:hover:text-secondary-100 transition-colors"
              >
                Login
              </Link>
              <a
                href="#contact"
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "inline-block text-center px-4 py-2.5 rounded-lg text-sm font-medium",
                  "bg-primary-600 text-white dark:bg-primary-500",
                  "hover:bg-primary-500 dark:hover:bg-primary-400 transition-all duration-300"
                )}
              >
                Join the Waitlist
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content — 6 sections */}
      <main>
        {/* 1. Hero */}
        <Hero />

        <SectionDivider variant="wave" />

        {/* 2. The Problem (condensed) */}
        <ProblemCondensed />

        <SectionDivider variant="dots" />

        {/* 3. Who It's For */}
        <WhoItsFor />

        {/* 4. Features (highlight) */}
        <FeaturesHighlight />

        {/* 5. Pricing (preview) */}
        <PricingPreview />

        <SectionDivider variant="gradient" />

        {/* 6. Dual CTA (contact + waitlist) */}
        <DualCTA />

        {/* CoreOps Managed Service callout */}
        <section className="relative py-14 sm:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-500/[0.02] to-transparent" />
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <p className="text-xs text-primary-600 dark:text-primary-400 font-medium tracking-[0.2em] uppercase mb-4">
              Don&apos;t Want to DIY?
            </p>
            <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-secondary-100 mb-4">
              Let <span className="text-primary-600 dark:text-primary-400">CoreOps</span> Handle It For You
            </h2>
            <p className="max-w-2xl mx-auto text-gray-500 dark:text-secondary-400 mb-8 leading-relaxed">
              CoreOps is our hands-on managed service — we coordinate your vendors, maintenance, and
              tenant communications so you don&apos;t have to. Powered by the Axis Datum platform.
            </p>
            <a
              href="https://coreops-landing.vercel.app"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "inline-flex items-center gap-3 px-8 py-4 rounded-xl",
                "bg-white dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08]",
                "hover:bg-gray-50 dark:hover:bg-white/[0.06] hover:border-primary-500/30",
                "shadow-sm hover:shadow-lg dark:shadow-none",
                "transition-all duration-300"
              )}
            >
              <img
                src="/images/coreops-logo.png"
                alt="CoreOps"
                className="w-10 h-10 rounded-lg object-contain"
              />
              <div className="flex flex-col items-start">
                <span className="text-sm font-semibold text-gray-900 dark:text-secondary-100">CoreOps Facilities Services</span>
                <span className="text-xs text-gray-400 dark:text-secondary-500">Veteran-owned managed coordination</span>
              </div>
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export { Landing };
